import { MediasAndArticleCountResponse } from "@coworker/app/src/components/RoomSettings/types/media";
import { MediaOrderedDetails } from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { MediaTypes } from "@coworker/app/src/components/RoomSettings/constants";

/**
 * Group media items by their media type
 */
export const groupMediasByType = (
  mediasData: MediasAndArticleCountResponse[]
): Record<string, MediasAndArticleCountResponse[]> => {
  return mediasData.reduce(
    (acc, media) => {
      const groupKey = media.mediaType || "Other";
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(media);
      return acc;
    },
    {} as Record<string, MediasAndArticleCountResponse[]>
  );
};

/**
 * Count unique media IDs from ordered details
 */
export const getUniqueMediaCount = (
  mediasOrderedDetails: MediaOrderedDetails[] | null
): number => {
  if (!Array.isArray(mediasOrderedDetails)) {
    return 0;
  }
  return new Set(mediasOrderedDetails.map((media) => media.id)).size;
};

/**
 * Count unique media IDs from media and article count response
 */
export const getUniqueMediaCountFromResponse = (
  mediasData: MediasAndArticleCountResponse[] | undefined
): number => {
  if (!Array.isArray(mediasData)) {
    return 0;
  }
  return new Set(mediasData.map((media) => media.mediaId)).size;
};

/**
 * Calculate total quantity from ordered details
 */
export const calculateTotalQuantity = <
  T extends
    | { pieces_ordered?: { quantity: number } }
    | { ptags_ordered?: { quantity: number } },
>(
  orderedDetails: T[] | null,
  field: "pieces_ordered" | "ptags_ordered"
): number => {
  if (!Array.isArray(orderedDetails)) return 0;
  return orderedDetails.reduce((acc, detail) => {
    const quantity = (detail as any)[field]?.quantity ?? 0;
    return acc + quantity;
  }, 0);
};

/**
 * Get composite key for media item
 */
export const getMediaItemKey = (
  item: MediasAndArticleCountResponse
): string => {
  return `${item.mediaId}:${item.articleId}`;
};

/**
 * Get media item title (handles room/parent media name logic)
 */
export const getMediaItemTitle = (
  item: MediasAndArticleCountResponse
): string => {
  return item.mediaType === MediaTypes.ROOM && item.parentMediaName
    ? item.parentMediaName
    : item.name;
};

/**
 * Get media item description (handles room grouping logic)
 */
export const getMediaItemDescription = (
  item: MediasAndArticleCountResponse
): string | undefined => {
  return item.mediaType === MediaTypes.ROOM && item.parentMediaName
    ? `${item.name}${item.groupName ? ` > ${item.groupName}` : ""}`
    : item.groupName;
};

/**
 * Find detail for a media item
 */
export const findMediaDetail = (
  item: MediasAndArticleCountResponse,
  orderedDetails: MediaOrderedDetails[] | null
): MediaOrderedDetails | undefined => {
  if (!Array.isArray(orderedDetails)) return undefined;
  return orderedDetails.find(
    (detail) =>
      item.mediaId === detail.id && item.articleId === detail.article_id
  );
};
